import { COUNTRY_SHORTCODES } from '../constants'
import { SupportedCountryCode } from '../models/types'
import {
  parsePhoneNumberFromString,
  isValidPhoneNumber as libIsValidPhoneNumber,
  CountryCode,
} from 'libphonenumber-js'
import { uniq } from 'lodash'
import { PhoneField } from '@doordash/component-fields'
import { WorldCountryCodes } from '@doordash/design-language'

/**
 * Tests validity of a given number. The number must be formatted
 * as an E164 number.
 *
 * @param {string} number - The E.164 formatted number to be tested for validity
 * @returns {boolean} Whether the number is valid
 */
export const isValidNumber = (number: string): boolean =>
  libIsValidPhoneNumber(number)

/**
 * E.164 is an ISO standard with shape: `+${countryCode}${subscriberNumber}`.
 *
 * This standard is handy for having to only reason about
 * one shape of phone number that is most widely supported.
 *
 * This function can also be used for validations, as it will return undefined
 * if the input phone number could not be formatted.
 *
 * @param {string} phoneNumber - The number to be formatted to E.164
 * @param {CountryCode} [countryCode] - Parse for the given country's number format
 * if provided, or iterate through all app supported country codes, and return the formatted
 * number of the first country (IMPORTANT: ordering is not guaranteed) that could
 * compose to a valid E.164 number.
 *
 * @returns {string | undefined} the E.164 formatted number of the input if it could be formatted,
 * undefined otherwise.
 */
export const formatToE164 = (
  phoneNumber: string,
  countryCode?: CountryCode
): string | undefined => {
  if (countryCode) {
    const parsed = parsePhoneNumberFromString(phoneNumber, {
      defaultCountry: countryCode,
    })
    if (
      parsed &&
      parsed.number &&
      parsed.country === countryCode &&
      parsed.isValid()
    ) {
      return parsed.number as string
    }
    return undefined
  }

  const possibleCountryCodes = getPossibleCountryCodesForPhone(phoneNumber)
  for (const code of possibleCountryCodes) {
    const parsed = parsePhoneNumberFromString(phoneNumber, {
      defaultCountry: code,
    })
    if (parsed && parsed.number && parsed.country == code && parsed.isValid()) {
      return parsed.number as string
    }
  }

  return undefined
}

/**
 * Required to ensure backwards compatibility, as the E164 formatter lib `phone`
 * is able to make better guesses at validity with the countryCode provided
 *
 * Example: '613 876 5498' without a countryCode is invalid, but '613 876 5498' with the 'CA'
 * countryCode is valid.
 */
export const getPossibleCountryCodesForPhone = (phoneNumber: string) => {
  const appSupportedCountryCodes = Object.keys(
    COUNTRY_SHORTCODES
  ) as SupportedCountryCode[]

  const possibleCountryCodes = appSupportedCountryCodes.reduce(
    (prev, countryCode) => {
      const parsed = parsePhoneNumberFromString(phoneNumber, countryCode)
      if (parsed.number && parsed.country === countryCode && parsed.isValid()) {
        prev.push(countryCode)
      }
      return prev
    },
    [] as SupportedCountryCode[]
  )

  return possibleCountryCodes
}

const sortedWorldCountryCodes = WorldCountryCodes.sort()

/**
 * Returns country codes that may be used to populate PhoneField components
 *
 * @param {string | SupportedCountryCode[]} [preferredCountryCodes] - country codes that will be promoted to the top. Defaults to an empty array
 * @param {boolean} [includeAllCountries] - to include the world's countries sorted alphabetically, or just the 'default supported ones'. Defaults to false
 */
export const getOrderedPhoneCountryCodes = ({
  preferredCountryCodes = [],
  includeAllCountries = false,
}: {
  preferredCountryCodes?: (string | SupportedCountryCode)[]
  includeAllCountries?: boolean
}): string[] => {
  const validPreferredCountryCodes = preferredCountryCodes.filter(code =>
    Boolean(code)
  )
  return includeAllCountries
    ? uniq([...validPreferredCountryCodes, ...sortedWorldCountryCodes])
    : uniq([
        ...validPreferredCountryCodes,
        ...PhoneField.DEFAULT_AVAILABLE_COUNTRY_CODES,
      ])
}
